import { inject, Service } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import type { OnyxMaybeArray } from '@onyx/angular';
import { OnyxModalService } from '@onyx/angular';
import { castArray, chain, isBoolean, isNumber, isString, round } from 'lodash';
import type { Observable } from 'rxjs';
import {
  catchError,
  concatMap,
  filter,
  from,
  fromEvent,
  map,
  of,
  scan,
  switchMap,
  tap,
} from 'rxjs';
import * as XLSX from 'xlsx';
import { XlsxHelper } from '../../../common/helpers/xlsx.helper';
import type { ImportModalData } from '../components/import/import-modal/import-modal.component';
import { ImportModalComponent } from '../components/import/import-modal/import-modal.component';
import { ImportHelper } from '../helpers/import.helper';
import type { ExportConfig } from '../interfaces/export-config';
import type {
  ImportConfig,
  ImportZodSchema,
} from '../interfaces/import-config';
import type {
  ImportParseProgress,
  ImportParseResult,
} from '../interfaces/import-parse-progress';
import { MAX_XLSX_FILE_NAME_LENGTH } from '../interfaces/max-xlsx-file-name-length';
import type { XlsxField } from '../interfaces/xlsx-field';

@Service()
export class XlsxService {
  private readonly translateService = inject(TranslateService);
  private readonly modalService = inject(OnyxModalService);
  private readonly importHelper = inject(ImportHelper);

  private readonly HEADER_ROWS = 2;

  public downloadTemplate(config: ExportConfig): void {
    this.downloadSheet(this.getSheetData(config), config.name, true);
  }

  public import<T extends ImportZodSchema>(config: ImportConfig<T>): void {
    const inputElement = document.createElement('input');
    inputElement.type = 'file';
    inputElement.accept = `application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,
                          application/vnd.ms-excel,
                          application/vnd.ms-excel.sheet.macroEnabled.12,
                          application/vnd.ms-excel.sheet.binary.macroEnabled.12,
                          text/csv,
                          application/vnd.oasis.opendocument.spreadsheet`;

    const import$ = (file: File): Observable<ImportParseProgress<T>> => {
      const translate = (name: XlsxField['name']) =>
        isString(name)
          ? this.translateService.instant(name)
          : name(this.translateService);

      const header = config.template.flatMap((section) =>
        section.fields.map((field) => field.key.toString()),
      );
      const fields = chain(config.template)
        .flatMap((section) => section.fields)
        .map((field) => [field.key, translate(field.name)])
        .fromPairs()
        .value();

      return of(file).pipe(
        switchMap((file) => {
          const reader = new FileReader();
          reader.readAsArrayBuffer(file);

          return fromEvent(reader, 'load').pipe(
            map(() => {
              const content = reader.result as ArrayBuffer;
              const workbook = XLSX.read(content, { type: 'array' });
              const sheet = workbook.Sheets[workbook.SheetNames[0]];
              const data = XLSX.utils.sheet_to_json(sheet, {
                header,
                defval: null,
              });

              return data.slice(this.HEADER_ROWS);
            }),
          );
        }),
        switchMap((data) => {
          if (!data.length) {
            return of({
              loading: false,
              progress: 0,
              total: 0,
              ok: true,
              data: [],
              errors: [],
            } satisfies ImportParseProgress<any>);
          }

          const schema = config.schema(this.importHelper);
          const parse$ = (row: unknown, index: number) =>
            from(schema.safeParseAsync(row)).pipe(
              map((result): ImportParseResult<any> => {
                if (result.success) {
                  return {
                    ok: true,
                    data: [{ index, ...result.data }],
                    errors: [],
                  };
                }
                return {
                  ok: false,
                  data: [],
                  errors: result.error.issues.map((issue) => ({
                    index,
                    section: translate(
                      config.template.find((section) =>
                        section.fields.some(
                          (field) => field.key === issue.path[0],
                        ),
                      )?.name || 'N/A',
                    ),
                    field: fields[issue.path[0]?.toString()] ?? 'N/A',
                    issue,
                  })),
                };
              }),
              catchError((error: Error) =>
                of({
                  ok: false,
                  data: [],
                  errors: [
                    {
                      index,
                      section: 'N/A',
                      field: 'N/A',
                      issue: {
                        code: 'custom' as any,
                        message: `Unexpected error: ${error.message}`,
                        path: [],
                      },
                    },
                  ],
                }),
              ),
            );

          return from(data).pipe(
            concatMap((row, index) =>
              parse$(row, index + this.HEADER_ROWS + 1).pipe(
                map((result) => ({ index: index + 1, result })),
              ),
            ),
            scan(
              (progress, result) => {
                progress.results.push(result.result);
                progress.index = result.index;
                return progress;
              },
              { index: 0, results: [] as ImportParseResult<any>[] },
            ),
            map((progress): ImportParseProgress<any> => {
              if (progress.index < data.length) {
                return {
                  loading: true,
                  progress: progress.index,
                  total: data.length,
                };
              }

              const { data: parsedData, errors } = progress.results
                .slice(1)
                .reduce((result, item) => {
                  result.data.push(...item.data);
                  result.errors.push(...item.errors);
                  return result;
                }, progress.results[0]);
              return {
                loading: false,
                progress: data.length,
                total: data.length,
                ok: errors.length === 0,
                data: parsedData,
                errors,
              };
            }),
          );
        }),
      );
    };

    fromEvent(inputElement, 'change')
      .pipe(
        map(() => inputElement.files?.[0]),
        tap(() => inputElement.remove()),
        filter((file) => !!file),
        tap((file) =>
          this.modalService.open<ImportModalData>(ImportModalComponent, {
            ...config,
            result$: import$(file),
          }),
        ),
      )
      .subscribe();
    inputElement.click();
  }

  public export(
    config: ExportConfig,
    items: OnyxMaybeArray<any>,
    decimalPlacesMap?: Record<string, number>,
  ): void {
    const fields = config.template.flatMap(({ fields }) => fields);
    const rows = castArray(items).map((item) => {
      return fields.map((field) => {
        const value = item[field.key];
        if (value == null || value === '') return null;

        if (isBoolean(value)) {
          return this.translateService.instant(
            `labels.${value ? 'yes' : 'no'}`,
          );
        }

        if (isString(value)) return this.translateService.instant(value);

        if (isNumber(value)) {
          const decimalPlaces = decimalPlacesMap?.[field.key] ?? 0;
          return decimalPlaces > 0
            ? round(value / 10 ** decimalPlaces, decimalPlaces)
            : value;
        }

        return value ?? null;
      });
    });

    this.downloadSheet([...this.getSheetData(config), ...rows], config.name);
  }

  private getSheetData(config: ExportConfig): (string | null)[][] {
    const sectionsHeader = config.template.flatMap((section) => [
      `${this.translateService.instant(section.name)}${section.required ? ' *' : ''}`,
      ...(Array.from({ length: section.fields.length - 1 }).fill(
        null,
      ) as null[]),
    ]);
    const fieldsHeader = config.template.flatMap((section) =>
      section.fields.map((field) => {
        const name = isString(field.name)
          ? this.translateService.instant(field.name)
          : field.name(this.translateService);

        return `${name}${field.required ? ' *' : ''}`;
      }),
    );

    return [sectionsHeader, fieldsHeader];
  }

  private downloadSheet(data: any[][], name: string, isTemplate = false): void {
    const worksheet = XLSX.utils.aoa_to_sheet(data);
    XlsxHelper.setAutoWidth(worksheet, data);
    XlsxHelper.setHeaderMerges(worksheet, data[0]);

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      workbook,
      worksheet,
      this.translateService
        .instant(isTemplate ? 'labels.template' : name)
        .slice(0, MAX_XLSX_FILE_NAME_LENGTH),
    );

    const workbookName = `${this.translateService.instant(name)}${isTemplate ? ` (${this.translateService.instant('labels.template').toLowerCase()})` : ''}`;
    XLSX.writeFile(workbook, `${workbookName}.xlsx`);
  }
}
